import { Facility, Spot, Vector2 } from "@app/shared";
import { useEffect, useRef, useState } from "react";
import usePlacesAutocomplete, { getDetails, getGeocode, getLatLng } from "use-places-autocomplete";
import { ModelHelpers } from "../../hooks/useModel";
import { Input, InputRow, NumericInput, SelectTimezone } from "./inputs";
import { PlaceSuggestions, getAddressDescription, parseDetailsIntoAddress } from "./maps/PlaceSuggestions";
import { LocationMapPicker } from "./maps/LocationMapPicker";
import { RequiresGoogleMaps } from "./maps/RequiresGoogleMaps";

type EditFacilityDetailsInputsProps = {
    model: Partial<Facility>;
    helpers: ModelHelpers<Facility>
};

function renderAddressKey(model: Partial<Facility>): string {
    return (
        model.address1 + "|" +
        model.state + "|" +
        model.city + "|" +
        model.zip + "|" +
        model.country
    );
}

const EditFacilityDetailsInputsContent: React.FC<EditFacilityDetailsInputsProps> = ({ model, helpers }) => {
    const [selectedFacility, setSelectedFacility] = useState<string>(renderAddressKey(model));
    const isInitializedRef = useRef(false);

    const places = usePlacesAutocomplete({
        initOnMount: false,
        defaultValue: model.address1 ?? "",
        requestOptions: {
            /* Define search scope here */
        },
        debounce: 300,
    });

    useEffect(() => {
        if (!isInitializedRef.current && typeof google !== 'undefined' && google.maps && google.maps.places) {
            places.init();
            isInitializedRef.current = true;
        }
    }, [places.init]);

    useEffect(() => {
        if (!isInitializedRef.current) return;

        let query = [
            model.address1,
            model.city,
        ]
            .filter(e => e)
            .join(", ");

        if (query === "") {
            places.clearSuggestions();
        } else {
            if (model.country) {
                query += ", " + model.country;
            }

            if (model.state) {
                query += ", " + model.state;
            }

            places.setValue(query);
        }
    }, [model.address1, model.city, model.country, model.state]);


    async function handlePlaceSelect(placeId: string) {
        const getDetailsParams = {
            placeId: placeId,
            fields: ['address_component', 'geometry']
        }
        var details = await getDetails(getDetailsParams);

        if (typeof details === 'string') {
            console.error("Unexpected string result from getDetails");
            return;
        }

        var address = parseDetailsIntoAddress(details as google.maps.places.PlaceResult);
        helpers.updateModel("address1", address.street);
        helpers.updateModel("city", address.city);
        helpers.updateModel("state", address.state);
        helpers.updateModel("city", address.city);
        helpers.updateModel("zip", address.zipCode);
        helpers.updateModel("country", address.country);

        setSelectedFacility(
            address.street + "|" +
            address.state + "|" +
            address.city + "|" +
            address.zipCode + "|" +
            address.country
        );

        places.clearSuggestions();

        if (details?.geometry?.location) {
            const lat = details.geometry.location.lat();
            const lng = details.geometry.location.lng();
            helpers.updateModel("originReferencePointLatLng",
                { points: [new Vector2({ x: lng, y: lat })] } as Spot)
        } else {
            console.warn("No geometry found in place details, falling back to geocoding");
            // fallback to geocoding if geometry is not available
            getGeocode({ address: getAddressDescription(address) })
                .then((results) => getLatLng(results[0]))
                .then(({ lat, lng }) => {
                    helpers.updateModel("originReferencePointLatLng",
                        { points: [new Vector2({ x: lng, y: lat })] } as Spot)
                })
                .catch((error) => {
                    console.error("Error handling place select", error);
                });
        }
    };


    const handleLonChange = (val: number | undefined) => {
        helpers.updateModel("originReferencePointLatLng", { points: [new Vector2({ x: val ?? 0, y: model.originReferencePointLatLng?.points?.[0]?.y ?? 0 })] } as Spot)
    };

    const handleLatChange = (val: number | undefined) => {
        helpers.updateModel("originReferencePointLatLng", { points: [new Vector2({ x: model.originReferencePointLatLng?.points?.[0]?.x ?? 0, y: val ?? 0 })] } as Spot)
    };

    const handleMapLocationChange = (lat: number, lng: number) => {
        helpers.updateModel("originReferencePointLatLng", { points: [new Vector2({ x: lng, y: lat })] } as Spot);

        if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results?.[0]) {
                    const result = results[0];
                    const addressComponents = result.address_components;

                    let street = '';
                    let city = '';
                    let state = '';
                    let zip = '';
                    let country = '';

                    addressComponents?.forEach(component => {
                        const types = component.types;

                        if (types.includes('street_number')) {
                            street = component.long_name + ' ';
                        }
                        if (types.includes('route')) {
                            street += component.long_name;
                        }
                        if (types.includes('locality')) {
                            city = component.long_name;
                        }
                        if (types.includes('administrative_area_level_1')) {
                            state = component.short_name;
                        }
                        if (types.includes('postal_code')) {
                            zip = component.long_name;
                        }
                        if (types.includes('country')) {
                            country = component.long_name;
                        }
                    });

                    // update all fields when user interacts with the map
                    if (street) {
                        helpers.updateModel("address1", street.trim());
                    }
                    if (city) {
                        helpers.updateModel("city", city);
                    }
                    if (state) {
                        helpers.updateModel("state", state);
                    }
                    if (zip) {
                        helpers.updateModel("zip", zip);
                    }
                    if (country) {
                        helpers.updateModel("country", country);
                    }

                    setSelectedFacility(
                        street.trim() + "|" +
                        state + "|" +
                        city + "|" +
                        zip + "|" +
                        country
                    );
                }
            });
        }
    };

    return (
        <>

            <InputRow>
                <Input label="Location Name" {...helpers.bindingsFor("name")} />
            </InputRow>
            <InputRow>
                <Input label="Client Location Code" {...helpers.bindingsFor("locationCode")} />
            </InputRow>
            <InputRow>
                <SelectTimezone label="Location TimeZone" {...helpers.bindingsFor("localTimezoneId")} />
            </InputRow>
            <InputRow>
                <Input label="Address" {...helpers.bindingsFor("address1")} />
            </InputRow>
            {places.suggestions.status === "OK" && renderAddressKey(model) !== selectedFacility &&
                <InputRow>
                    <PlaceSuggestions
                        suggestions={places.suggestions.data}
                        onClick={placeId => handlePlaceSelect(placeId)}
                    />
                </InputRow>
            }
            <InputRow>
                <Input {...helpers.bindingsFor("address2")} />
            </InputRow>
            <InputRow addlClasses="grid grid-cols-3 gap-2">
                <Input label="City" {...helpers.bindingsFor("city")} />
                <Input label="State" {...helpers.bindingsFor("state")} />
                <Input label="Zipcode" {...helpers.bindingsFor("zip")} />
            </InputRow>
            <InputRow>
                <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pick Location on Map (click or drag marker to set location)
                    </label>
                    <div className="text-xs text-gray-500 mb-2">
                        You can search for an address above, or click/drag on the map to set the exact location
                    </div>
                    <LocationMapPicker
                        latitude={model.originReferencePointLatLng?.points?.[0]?.y}
                        longitude={model.originReferencePointLatLng?.points?.[0]?.x}
                        onLocationChange={handleMapLocationChange}
                        height={350}
                    />
                </div>
            </InputRow>
            <InputRow addlClasses="grid grid-cols-2 gap-2">
                <NumericInput label="Latitude" value={(model.originReferencePointLatLng?.points?.[0]?.y ?? "") + ""} onChange={handleLatChange} />
                <NumericInput label="Longitude" value={(model.originReferencePointLatLng?.points?.[0]?.x ?? "") + ""} onChange={handleLonChange} />
            </InputRow>
        </>
    );
};

export const EditFacilityDetailsInputs: React.FC<EditFacilityDetailsInputsProps> = (props) => {
    return (
        <RequiresGoogleMaps>
            <EditFacilityDetailsInputsContent {...props} />
        </RequiresGoogleMaps>
    );
};